var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback } from 'react';
import classNames from "classnames";
import get from "lodash/get";
import { css } from 'aphrodite/no-important';
import debounce from "lodash/debounce";
import { publish } from 'conversional-callbacks';
import { Events, JourneysSelectors, TrackingActions, useAppDispatch, useAppSelector } from 'conversional-journey';
import { ComponentClass } from 'conversional-theme';
import { sendToHost } from "conversional-iframe-communication";
import Icon from "../icons/Icon";
import { getStyles } from './Button.styles';
import { ICONS } from "../icons/IconList";
var CLICK_EVENT = 'Button.clicked';
var CLICK_INTERVAL = 1500;
export var TYPES = {
    INLINE: 'inline',
    OUTLINE: 'outline',
    OUTLINE_BRAND: 'outline_brand',
    OUTLINE_DARK_COLOR: 'outline_dark_color',
    OUTLINE_BRAND_CARD: 'outline_brand_card',
    OUTLINE_BRAND_CARD_ACTIVE: 'outline_brand_card_active',
    PRIMARY: 'primary',
    SECONDARY: 'secondary',
    GHOST: 'ghost',
    STANDARD: 'standard',
    TEXT: 'text',
    SUCCESS: 'success'
};
var OneLineButtonText = function (_a) {
    var label = _a.label, computedStyles = _a.computedStyles;
    return _jsx("span", { className: computedStyles.label, children: label });
};
var TwoLineButtonText = function (_a) {
    var label = _a.label, subtitleLabel = _a.subtitleLabel, computedStyles = _a.computedStyles;
    return (_jsxs("div", { className: computedStyles.labelContainer, children: [_jsx("span", { className: computedStyles.labelWithSubtitle, children: label }), _jsx("span", { className: computedStyles.subtitleLabel, children: subtitleLabel })] }));
};
export var BaseButton = function (_a) {
    var _b;
    var _c, _d;
    var onClick = _a.onClick, type = _a.type, _e = _a.onClickUseCallbackDependencies, onClickUseCallbackDependencies = _e === void 0 ? [] : _e, disabled = _a.disabled, buttonType = _a.buttonType, buttonSize = _a.buttonSize, buttonClassName = _a.buttonClassName, containerClassName = _a.containerClassName, children = _a.children, block = _a.block, customStyles = _a.customStyles, styles = _a.styles, componentKey = _a.componentKey, callback = _a.callback, subtitle = _a.subtitle, label = _a.label, id = _a.id, prefixIcon = _a.prefixIcon, suffixIcon = _a.suffixIcon, baseButtonContainerStyles = _a.baseButtonContainerStyles, key = _a.key, eventTrackerMeta = _a.eventTrackerMeta, childrenContainerStyles = _a.childrenContainerStyles, subtitleLabel = _a.subtitleLabel;
    var dispatch = useAppDispatch();
    var theme = useAppSelector(JourneysSelectors.selectTheme);
    var trackEvent = function (event) { return dispatch(TrackingActions.event(event)); };
    // debounce the click function
    // note that the clicks are passed as a parameter to avoid repeating the initial value
    /**
     * We are using useCallback to cache function's definition in order to make
     * debouncing behavior work in a function component and do not reassign the function
     * again when re-rendering. Sometimes we need to use some state so we should pass that
     * `onClickUseCallbackDependencies` array to make sure the cache is invalidated whenever
     * the state is updated.
     * */
    var debouncedClick_DEPRECATE = useCallback(debounce(function () {
        executeOnClick();
    }, CLICK_INTERVAL, { leading: true, trailing: false, maxWait: CLICK_INTERVAL }), onClickUseCallbackDependencies);
    var executeOnClick = function () {
        var callbackEvent = get(callback, "event");
        if (callbackEvent) {
            sendToHost.publish(callbackEvent, {});
        }
        var toHostEvent = get(callback, "toHostEvent");
        if (toHostEvent) {
            sendToHost.toHost(toHostEvent);
        }
        publish(CLICK_EVENT, {}, componentKey);
        trackEvent(__assign(__assign({}, Events.BUTTON_CLICK), { label: componentKey || label || 'undefined', meta: eventTrackerMeta }));
        onClick();
    };
    var computedStyles = getStyles(theme, buttonType);
    var injected = css(customStyles);
    var getButtonText = function (label, subtitleLabel, computedStyles) {
        if (!subtitleLabel) {
            return _jsx(OneLineButtonText, { label: label, computedStyles: computedStyles });
        }
        return _jsx(TwoLineButtonText, { label: label, subtitleLabel: subtitleLabel, computedStyles: computedStyles });
    };
    return (_jsxs("div", { className: classNames(computedStyles.container, ComponentClass(baseButtonContainerStyles), (_b = {},
            _b[computedStyles.block] = block,
            _b), containerClassName), children: [_jsxs("button", { type: type, disabled: disabled, onClick: function () { return executeOnClick(); }, id: id, className: classNames(ComponentClass((_d = (_c = theme === null || theme === void 0 ? void 0 : theme.customizedStyles) === null || _c === void 0 ? void 0 : _c.elements) === null || _d === void 0 ? void 0 : _d.button), computedStyles.button, computedStyles[buttonType], computedStyles[buttonSize], ComponentClass(styles), injected, buttonClassName), children: [prefixIcon && (_jsx(Icon, { className: classNames(computedStyles.icon, computedStyles.prefixIcon), icon: get(ICONS, prefixIcon) })), children && _jsx("span", { className: ComponentClass(childrenContainerStyles), children: children }), getButtonText(label, subtitleLabel, computedStyles), suffixIcon && (_jsx(Icon, { className: classNames(computedStyles.icon, computedStyles.suffixIcon), icon: get(ICONS, suffixIcon) }))] }), subtitle && _jsx("span", { className: computedStyles.subtitle, children: subtitle })] }, key));
};
BaseButton.defaultProps = {
    buttonType: TYPES.STANDARD,
    onClick: function () {
    },
    onClickUseCallbackDependencies: []
};
export default BaseButton;
